import { FC, useEffect, useRef, useState } from "react";
import { styles } from "../stylesHome";
import { Box, Link, Typography } from "@mui/material";
import Popup from "@/components/UI/popup/Popup";
import theme from "@/theme";
import { IPost } from "@/store/api/postApiData";
import { useNavigate } from "react-router-dom";
import TwitterEmbed from "../../../components/TwitterEmbed";
import PostImagesGrid from "./PostImagesGrid";
import EmbeddedPost from "@/components/EmbeddedPost";
import ExternalPreview from "@/components/ExternalPreview";
import {
  isTicker,
  TICKER_REGEX,
} from "@/components/UI/modal/CreatePostModal/component/tickerSchema";
import { AtomicYouTube } from "@/components/YouTubeEmbed";

interface IPostContentProps {
  post: IPost;
  isImageValid: boolean;
  setIsImageValid: (value: boolean) => void;
  maxPreviews?: number;
}

const PostContent: FC<IPostContentProps> = ({ post }) => {
  const navigate = useNavigate();
  const contentRef = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const handleSymbolClick = (symbol: string) => {
    navigate(`/stock/${symbol}`);
  };
  useEffect(() => {
    if (window.twttr) {
      window.twttr.widgets.load();
    }
  }, [post.html]);

  const urlRegex = /(https?:\/\/[^\s]+)/i;
  const match = post.content.match(urlRegex);
  const firstUrl = match ? match[0] : null;

  const twitterRegex = /https?:\/\/(?:twitter\.com|x\.com)\/[^\/\s]+\/status\/(\d+)/i;
  const internalPostRegex =
    /https?:\/\/(?:wethemrkt\.com|wethermarket-develop-[^\/]+\.ondigitalocean\.app)\/post\/([a-f0-9-]+)/i;

  const youtubeRegex = /https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/i;

  let previewPart: {
    type: "tweet" | "post" | "external" | "youtube";
    id?: string;
    url: string;
  } | null = null;

  if (firstUrl) {
    const twitterMatch = twitterRegex.exec(firstUrl);
    const internalMatch = internalPostRegex.exec(firstUrl);
    const youtubeMatch = youtubeRegex.exec(firstUrl);

    if (twitterMatch) {
      previewPart = { type: "tweet", id: twitterMatch[1], url: firstUrl };
    } else if (internalMatch) {
      previewPart = { type: "post", id: internalMatch[1], url: firstUrl };
    } else if (youtubeMatch) {
      previewPart = { type: "youtube", id: youtubeMatch[1], url: firstUrl };
    } else {
      previewPart = { type: "external", url: firstUrl };
    }
  }

  let beforeText = post.content;
  let afterText = "";
  if (firstUrl) {
    const index = post.content.indexOf(firstUrl);
    beforeText = post.content.slice(0, index).replace(firstUrl, "").trim();
    afterText = post.content
      .slice(index + firstUrl.length)
      .replace(urlRegex, "")
      .trim();
  }

  const textContent = [beforeText, afterText].filter(Boolean).join("\n");
  const isClampable = !!textContent;

  const renderText = (text: string, keyPrefix: string) =>
    text.split(new RegExp(`(${TICKER_REGEX.source})`, "g")).map((subPart, subIndex) => {
      if (isTicker(subPart)) {
        return (
          <Popup symbol={subPart} type="company" key={`${keyPrefix}-${subIndex}`}>
            <Typography
              variant="h5"
              component="span"
              fontWeight={400}
              lineHeight="17px"
              fontSize={"14px"}
              color={theme.palette.custom.mainBlue}
              sx={{ cursor: "pointer", display: "inline" }}
              onClick={() => handleSymbolClick(subPart.slice(1))}
            >
              {subPart}
            </Typography>
          </Popup>
        );
      }
      return subPart;
    });

  const renderPreview = (part: NonNullable<typeof previewPart>) => {
    if (part.type === "tweet" && part.id) {
      return (
        <Box sx={{ margin: "0.5rem 0" }}>
          <TwitterEmbed id={part.id} />
        </Box>
      );
    }
    if (part.type === "post" && part.id) {
      return <EmbeddedPost uid={part.id} />;
    }
    if (part.type === "youtube" && part.id) {
      return (
        <Box sx={{ margin: "0.5rem 0" }}>
          <AtomicYouTube videoId={part.id} />
        </Box>
      );
    }
    if (part.type === "external") {
      return <Box sx={{ margin: "0.5rem 0" }}>{post.html && <ExternalPreview html={post.html} />}</Box>;
    }
    return null;
  };

  useEffect(() => {
    const el = contentRef.current;
    if (!el || expanded || !isClampable) return;
    const measure = () => setHasOverflow(el.scrollHeight > el.clientHeight + 1);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [post.content, post.html, expanded, isClampable]);

  return (
    <Box sx={styles.postContent}>
      {textContent && (
        <Typography
          ref={contentRef}
          variant="h5"
          component="p"
          fontWeight={400}
          lineHeight="17px"
          sx={{
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            whiteSpace: "pre-line",
            color: "#2A2D31",
            fontSize: "15px",
            ...(!expanded &&
              isClampable && {
                display: "-webkit-box",
                WebkitLineClamp: 7,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }),
          }}
        >
          {renderText(textContent, "text")}
        </Typography>
      )}

      {isClampable && (hasOverflow || expanded) && (
        <Typography
          component="span"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((prev) => !prev);
          }}
          sx={{
            display: "inline-block",
            mt: "2px",
            fontSize: "15px",
            fontWeight: 500,
            lineHeight: "17px",
            color: theme.palette.custom.mainBlue,
            cursor: "pointer",
          }}
        >
          {expanded ? "Show less" : "Show more"}
        </Typography>
      )}

      {previewPart && renderPreview(previewPart)}

      {post.attachments.length > 0 && <PostImagesGrid attachments={post.attachments} />}

      {post.sources.length > 0 && (
        <Typography variant="h5" component="p" fontWeight={500} lineHeight="1.5rem" mt={1}>
          Source:{" "}
          {post.sources.map((source, index) => (
            <Link
              key={index}
              href={source}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "inline-block",
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                verticalAlign: "bottom",
                mr: index < post.sources.length - 1 ? 1 : 0,
              }}
            >
              {source}
            </Link>
          ))}
        </Typography>
      )}
    </Box>
  );
};

export default PostContent;
